<!-- eslint-disable max-len -->
<style scoped>
  .project-concept-button {
    margin-top: 1px;
    margin-right: 7px;
    border: 0;
    padding-top: 8px;
    padding-right: 16px;
    padding-bottom: 8px;
    padding-left: 16px;
  }
</style>
<template>
  <div class='view-contents'>
    <div class="row title-row">
      <div class="col page-title">
        <a v-if='project' :href='`${config.insightly.projectUrl}${project.id}`' target='insightly'>{{project.projectName}}</a>
        <span v-if='project'>&nbsp;({{ project.approvalStatus }})&nbsp;</span>
        <b-link class='section-sub-title no-wrap' @click="downloadProjectPlanReport(parseInt($route.params.id, 10))"><b-icon icon='download' class='download-icon'/>Download Plan</b-link>
        <b-modal id="submitProjectModal" @ok="onSubmitProjectOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Confirm Project Submittal</span>
          </template>
          <p>Once submitted, you will not be able to make changes on the Goals, Plan Details, Project Actions, Incentive Payments, or Budget pages.</p>
          <p>(An annual budget can be updated and must be submitted once the Project is approved.)</p>
          <p>Are you sure you want to submit this Project for approval?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id="approveProjectModal" @ok="onApproveProjectOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Confirm Project Approval</span>
          </template>
          <p>Upon Project approval, the total cost for this project will be locked in at {{ budgetTotalAmount | toCurrency }} (includes TSWCD costs, Partner match, Landowner cost share and incentive payments).</p>
          <p>Planners will be able to submit their first annual budget once the Project is approved.</p>
          <p>Are you sure you want to approve this project?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id="rejectProjectModal" @ok="onRejectProjectOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Confirm Project Rejection</span>
          </template>
          <p>If you reject this Project, the Planner will be able to edit project components, and re-submit if appropriate.</p>
          <p>Are you sure you want to reject this Project?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id="cancelCompleteProjectErrorModal" ok-only>
          <template v-slot:modal-title>
            <span v-if="cancelCompleteProjectErrorModalType==='Cancelled'" class='project-modal-danger'>Error Cancelling Project!</span>
            <span v-else class='project-modal-danger'>Error Closing Project!</span>
          </template>
          <div v-if="cancelCompleteProjectErrorModalReason==='OpenWorkOrder'">
            <p>Project cannot be marked as {{cancelCompleteProjectErrorModalType}}: One or more Work Orders are open.</p>
            <p>Please ensure all Work Orders are Completed or Cancelled.</p>
          </div>
          <div v-if="cancelCompleteProjectErrorModalReason==='UnpaidBill'">
            <p>Project cannot be marked as {{cancelCompleteProjectErrorModalType}}: One or more Bills are unpaid.</p>
            <p>Please ensure all Bills are either Paid or Voided.</p>
          </div>
          <div v-if="cancelCompleteProjectErrorModalReason==='UnpaidIncentivePayment'">
            <p>Project cannot be marked as {{cancelCompleteProjectErrorModalType}}: One or more Incentive Payments planned for the Project remain unpaid.</p>
            <p>Please indicate paid date or delete the planned payment.</p>
          </div>
        </b-modal>
        <b-modal id="completeProjectModal" @ok="onCompleteProjectOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Confirm Project Completion</span>
          </template>
          <p>Projects cannot be edited in Acorn once completed.</p>
          <p>Are you sure you want to Complete this Project?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id="cancelProjectModal" @ok="onCancelProjectOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Confirm Project Cancellation</span>
          </template>
          <p>Projects cannot be edited in Acorn once cancelled.</p>
          <p>Are you sure you want to Cancel this Project?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id="projectWorkOrderPlannedModal" @ok="onProjectWorkOrderPlannedModalOkClick()">
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Unapproved Funding Years!</span>
          </template>
          <p>This project still has scopes of work planned for the future.</p>
          <p>Are you sure you want to Complete this Project?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-modal id='invalidPlanDetailsModal' centered scrollable ok-only>
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Invalid Plan Details!</span>
          </template>
          <div>
            <p>One or more Project Plan Detail items does not meet minimum length requirements!</p>
            <p>Please use the Project's Plan Details tab to update them.</p>
            <p>Please save your changes after making them.</p>
          </div>
        </b-modal>
        <b-modal id='invalidStartDateModal' centered scrollable ok-only>
          <template v-slot:modal-title>
            <span class='project-modal-warning'>Invalid Start Date!</span>
          </template>
          <div>
            <p>This Project does not have a Start Date!</p>
            <p>Please update the Project's &quot;Start Date&quot; field in Insightly.</p>
          </div>
        </b-modal>
        <b-modal id='insufficientFundsModal' centered scrollable size='xl' title="Insufficient Funds" ok-only>
          <div>
            <p>There are insufficient funds to cover the requested costs for this Project for one or more funding sources.</p>
            <p>Please make adjustments to amounts being requested, funding sources used, and/or increase the available funds for the funding sources used.</p>
          </div>
          <div v-if='projectBudget && fundingSources'>
            <b-container>
              <b-row>
                <b-col cols='3' class='insufficient-funds-header'>Funding Source</b-col>
                <b-col cols='9'>
                  <b-container>
                    <b-row>
                      <b-col cols='3' class='insufficient-funds-header insufficient-funds-numeric'>Total "New Projects Budget"</b-col>
                      <b-col cols='3' class='insufficient-funds-header insufficient-funds-numeric'>Encumbered across all Projects</b-col>
                      <b-col cols='2' class='insufficient-funds-header insufficient-funds-numeric'>Remaining</b-col>
                      <b-col cols='2' class='insufficient-funds-header insufficient-funds-numeric'>Requested for this Project</b-col>
                      <b-col cols='2' class='insufficient-funds-header insufficient-funds-numeric'>Difference</b-col>
                    </b-row>
                  </b-container>
                </b-col>
              </b-row>
              <b-row v-for="item in projectBudgetValidationResults" :key="item.fundingSourceId">
                <template v-if="item.totalDifference < 0">
                  <b-col cols='3'>{{item.fundingSourceName}}</b-col>
                  <b-col cols='9'>
                    <b-container>
                      <b-row>
                        <b-col cols='3' class='insufficient-funds-numeric'>{{item.totalBudgeted | toCurrency}}</b-col>
                        <b-col cols='3' class='insufficient-funds-numeric'>{{item.totalEncumbered | toCurrency}}</b-col>
                        <b-col cols='2' class='insufficient-funds-numeric'>{{item.totalAvailable | toCurrency}}</b-col>
                        <b-col cols='2' class='insufficient-funds-numeric'>{{item.totalRequested | toCurrency}}</b-col>
                        <b-col cols='2' class='insufficient-funds-numeric'>({{Math.abs(item.totalDifference) | toCurrency}})</b-col>
                      </b-row>
                    </b-container>
                  </b-col>
                </template>
              </b-row>
            </b-container>
          </div>
        </b-modal>
      </div>
      <div class="col">
        <b-button
          id='submitProjectConceptButton'
          class="project-concept-button rounded-pill float-right-button"
          variant='primary'
          :title="getSubmitProjectConceptTitle"
          v-b-modal="'submitProjectConceptModal'"
          v-if="isSubmitter && isSubmittable"
          :disabled="!isSubmitProjectConceptEnabled"
        >
          <span>Submit Concept</span>
        </b-button>
        <b-button
          class="project-concept-button rounded-pill float-right-button"
          variant='primary'
          title='Approve'
          v-if="isApprover && isApprovable"
          @click="onApproveProjectConceptClick"
        >
          <span>Approve</span>
        </b-button>
        <b-button
          class="project-concept-button rounded-pill float-right-button"
          variant="danger"
          title='Reject'
          v-if="isRejecter && isRejectable"
          @click="onRejectProjectConceptClick"
        >
          <span>Reject</span>
        </b-button>
        <b-modal id="submitProjectConceptModal" title="Confirm Project Concept Submittal" @ok="onSubmitProjectConceptOkClick()">
          <p>Once submitted, you will not be able to make changes to this Project Concept section.</p>
          <p>Are you sure you want to submit this Project Concept for review?</p>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>No</b-button>
            <b-button size="sm" variant="success" @click="ok" class='btn-success'>Yes</b-button>
          </template>
        </b-modal>
        <b-button
          id='submitProjectButton'
          class="project-concept-button rounded-pill float-right-button"
          variant='primary'
          v-b-tooltip.hover
          v-bind:title="submitProjectButtonTitle"
          v-if="isSubmitter && isProjectSubmittable"
          :disabled="!isSubmitProjectEnabled"
          @click="onSubmitProjectClick"
        >
          <span>Submit Project</span>
        </b-button>
        <b-button
          class="project-concept-button rounded-pill float-right-button"
          variant='primary'
          title='Approve Project'
          v-if="isApprover && isProjectApprovable"
          :disabled="isValidating"
          @click="onApproveProjectClick"
        >
          <span>Approve</span>
        </b-button>
        <b-button
          class="project-concept-button rounded-pill float-right-button"
          variant="danger"
          title='Reject Project'
          v-b-modal="'rejectProjectModal'"
          v-if="isRejecter && isProjectRejectable"
          :disabled="isValidating"
        >
          <span>Reject</span>
        </b-button>
        <span v-if='isApprover'>
          <b-button
            class="project-concept-button rounded-pill float-right-button"
            variant='primary'
            title='Complete Project'
            v-if="isProjectCompletable"
            @click="onCompleteProjectClick"
          >
            <span>Complete</span>
          </b-button>
          <b-button
            class="project-concept-button rounded-pill float-right-button"
            variant="danger"
            title='Cancel Project'
            v-if="isProjectCancelable"
            @click="onCancelProjectClick"
          >
            <span>Cancel</span>
          </b-button>
        </span>
      </div>
    </div>
    <b-nav
      class="section-header project-subheader-line"
      content-class="mt-3"
      pills rounded-pill
      align='right'>
      <b-nav-item :active-class="'rounded-pill'" :active="$route.name === 'ProjectOverview'" :to="{ name: 'ProjectOverview', params: { id: parseInt(this.$route.params.id, 10) } }">
        Overview
      </b-nav-item>
      <b-nav-item :active-class="'rounded-pill'" :active="$route.name === 'ProjectContacts'" :to="{ name: 'ProjectContacts', params: { id: parseInt(this.$route.params.id, 10) } }">
        Contacts
      </b-nav-item>
      <b-nav-item v-if="isTabbable" :active-class="'rounded-pill'" :active="$route.name === 'ProjectGoals'" :to="{ name: 'ProjectGoals', params: { id: parseInt(this.$route.params.id, 10) } }">
        Goals
      </b-nav-item>
      <b-nav-item v-if="isTabbable" :active-class="'rounded-pill'" :active="$route.name === 'PlanDetails'" :to="{ name: 'PlanDetails', params: { id: parseInt(this.$route.params.id, 10) } }">
        Plan Details
      </b-nav-item>
      <b-nav-item v-if="isTabbable" :active-class="'rounded-pill'" :active="$route.name === 'ProjectActions'" :to="{ name: 'ProjectActions', params: { id: parseInt(this.$route.params.id, 10) } }">
        Project Actions
      </b-nav-item>
      <b-nav-item v-if="isTabbable" :active-class="'rounded-pill'" :active="$route.name === 'ProjectBudget'" :to="{ name: 'ProjectBudget', params: { id: parseInt(this.$route.params.id, 10) } }">
        Budget
      </b-nav-item>
      <b-nav-item v-if="isTabbable && project && project != null && (project.category === 'ECREP Project' || project.category === 'VEGBAC Project' || project.category === 'Conservation Easement')" :active-class="'rounded-pill'" :active="$route.name === 'ProjectIncentives'" :to="{ name: 'ProjectIncentives', params: { id: parseInt(this.$route.params.id, 10) } }">
        Incentive Payments
      </b-nav-item>
      <b-nav-item v-if="project && (project.approvalStatus == 'Approved' || project.approvalStatus == 'Completed' || project.approvalStatus == 'Cancelled')" :active-class="'rounded-pill'" :active="$route.name === 'ProjectWorkorders'" :to="{ name: 'ProjectWorkorders', params: { id: parseInt(this.$route.params.id, 10) } }">
        <span>Work&nbsp;Orders</span>
      </b-nav-item>
      <b-nav-item v-if="isTabbable && isProjectOutcomesEnabled" :active-class="'rounded-pill'" :active="$route.name === 'ProjectOutcomes'" :to="{ name: 'ProjectOutcomes', params: { id: parseInt(this.$route.params.id, 10) } }">
        Outcomes
      </b-nav-item>
    </b-nav>
    <div>
      <b-overlay :show="formLoading || isDownloading || isValidating"><router-view/></b-overlay>
    </div>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks, prefer-destructuring */
import { mapState } from 'vuex';
import api from '@/api';
import FiscalYear from '@/mixins/fiscalYear';
import ProjectApprovalManager from '@/mixins/projectApprovalManager';
import ProjectApprovalMembershipManager from '@/mixins/projectApprovalMembershipManager';
import ProjectTotalsCalculator from '@/mixins/projectTotalsCalculator';

export default {
  name: 'Project',
  mixins: [
    FiscalYear,
    ProjectApprovalManager,
    ProjectApprovalMembershipManager,
    ProjectTotalsCalculator],
  computed: {
    ...mapState(['config']),
    ...mapState('Membership', {
      memberOf: (state) => state.memberOf,
    }),
    ...mapState('Project', {
      encumberedFundsForApprovedBudgets: (state) => state.encumberedFundsForApprovedBudgets,
      fundingSourceEncumberedFunds: (state) => state.fundingSourceEncumberedFunds,
      fundingSourceExpendedFunds: (state) => state.fundingSourceExpendedFunds,
      fundingSources: (state) => state.fundingSources,
      fundsPerFiscalYear: (state) => state.fundsPerFiscalYear.funds,
      project: (state) => state.project,
      projectBudget: (state) => state.projectBudget,
      projectConcept: (state) => state.projectConcept,
      projectGeometry: (state) => state.projectGeometry,
      projectGoalSelections: (state) => state.projectGoalSelections,
      projectIncentivePayments: (state) => state.projectIncentivePayments,
      projectIncentiveTypes: (state) => state.projectIncentiveTypes,
      projectObjectiveSelections: (state) => state.projectObjectiveSelections,
      projectObjectiveAccomplishmentTargetSelections: (state) => state.projectObjectiveAccomplishmentTargetSelections,
      projectPlanDetails: (state) => state.currentPlanDetails,
      projectPractices: (state) => state.projectPractices,
      projectSnapshots: (state) => state.projectSnapshots,
      currentProjectSnapshots: (state) => state.currentProjectSnapshots,
      projectStatus: (state) => state.projectStatus,
      projectWorkorders: (e) => e.projectWorkorders,
      projectWorkorderBills: (e) => e.projectWorkorderBills,
    }),
    budgetFundingSources() {

      if (!this.projectBudget || !this.projectBudget.projectBudgetYears || !this.fundingSources) {
        return [];
      }

      const budgetFundingSourceIds = [];

      this.projectBudget.projectBudgetYears.forEach((projectBudgetYear) => {
        projectBudgetYear.projectBudgetLineItems.forEach((projectBudgetLineItem) => {
          budgetFundingSourceIds.push(projectBudgetLineItem.fundingSourceId);
        });
      });

      const budgetFundingSources = this.fundingSources.map(
        (e) => ({ id: e.recorD_ID, name: e.recorD_NAME }),
      ).filter(
        (e) => budgetFundingSourceIds.includes(e.id),
      ).sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name === b.name) return 0;
        return 1;
      });

      return budgetFundingSources;
    },
    insufficientFunds() {
      const insufficientFunds = this.projectBudgetValidationResults.filter((e) => e.totalDifference < 0).length > 0;
      return insufficientFunds;
    },
  },
  data() {
    return {
      fiscalYear: null,
      formLoading: false,
      fundingSourceBudgets: null,
      identityManager: null,
      isDownloading: false,
      isValidating: false,
      projectBudgetValidationResults: [],
      projectFundsPerFiscalYear: null,
      submitStatus: null,
      totalProjectFundsPerFiscalYear: null,
      submitProjectButtonTitle: 'Submit Project',
      approveProjectModal: 'approveProjectModal',
      cancelCompleteProjectErrorModal: 'cancelCompleteProjectErrorModal',
      cancelCompleteProjectErrorModalReason: '',
      cancelCompleteProjectErrorModalType: '',
      cancelProjectModal: 'cancelProjectModal',
      completeProjectModal: 'completeProjectModal',
      insufficientFundsModal: 'insufficientFundsModal',
      invalidPlanDetailsModal: 'invalidPlanDetailsModal',
      invalidStartDateModal: 'invalidStartDateModal',
      projectWorkOrderPlannedModal: 'projectWorkOrderPlannedModal',
      submitProjectModal: 'submitProjectModal',
    };
  },
  mounted() {
    (async () => {
      this.formLoading = true;
      Promise.all([
        this.$store.dispatch('fetchConfiguration'),
        this.$store.dispatch('Membership/fetchMemberOf'),
        this.$store.dispatch('Project/getProject', parseInt(this.$route.params.id, 10)),
      ]).then(() => {
        this.submitStatus = this.config.projectOverviewPage.submitStatus;
        this.fiscalYear = this.getFiscalYear(this.project.startDate);
        this.load();
        this.formLoading = false;
      });
    })();
  },
  methods: {
    downloadProjectPlanReport(id) {
      this.isDownloading = true;
      api.get(
        `report/projectPlan/${id}`,
        { responseType: 'blob' },
      ).then((response) => {
        const href = window.URL.createObjectURL(
          new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
          ),
        );
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', 'projectplan.docx');
        document.body.appendChild(link);
        link.click();
        this.isDownloading = false;
      }).catch((exception) => {
        this.isDownloading = false;
        console.error(exception);
      });
    },
  },
};
</script>
